import type {
  BulkWeightValue,
  KindCondition,
  ProductThreshold,
  SpecialRules,
  SpecialRuleSection,
  SpecialRuleSetsFor,
  SpecialType,
  SpecialTypeWithRules,
} from '@iheartjane/types';
import compact from 'lodash-es/compact';

import type {
  ConditionRangeValue,
  ConditionValue,
  ConditionSelection,
  ConditionSet,
} from '../conditions.types';
import { encodeKey } from './conditionKeys';
import {
  CONDITION_SELECTION_TYPES,
  unitValueKey,
} from './conditionSelection.util';
import { updateConditionSelection } from './updateConditionSelection';

export const EMPTY_CONDITION_SET = [
  {
    applyTo: 'attributes',
    selections: {
      excludes: {},
      includes: {},
    },
  },
];

/** Any rule set family — only these keys are read, whatever the rules hold */
type RuleSetLike = {
  excludes?: object;
  includes?: object;
  product_threshold?: ProductThreshold;
};

const toRange = ({
  maximum_price,
  minimum_price,
}: ProductThreshold): ConditionRangeValue => ({
  ...(maximum_price != null && { max: maximum_price }),
  ...(minimum_price != null && { min: minimum_price }),
});

/** Rule keys whose stored shape differs from what the editor selects on */
const TRANSFORMS: Record<string, (value: unknown) => ConditionValue> = {
  kinds: (value) =>
    (value as KindCondition[]).map(({ brand_subtype, kind, root_subtype }) =>
      encodeKey(compact([kind, root_subtype, brand_subtype]))
    ),
  // ids arrive as numbers; the editor selects on strings
  product_ids: (value) => (value as number[]).map(String),
  // one range per weight, keyed the way the editor's options are
  bulk_weight_values: (value) =>
    Object.fromEntries(
      (value as BulkWeightValue[]).map((weight) => [
        unitValueKey(weight.weight_value),
        toRange(weight),
      ])
    ),
};

/** Rule keys the editor names differently */
const KEY_MAPPING: Record<string, string> = {
  bulk_weight_values: 'weight_and_price',
};

const toConditionValue = (key: string, value: unknown): ConditionValue =>
  TRANSFORMS[key] ? TRANSFORMS[key](value) : (value as ConditionValue);

export const getConditionSelections = <R extends RuleSetLike>(
  ruleSets: ['any', R, ...R[]]
): ConditionSelection[] => {
  const [, ...entries] = ruleSets;

  return entries.map((ruleSet) => {
    const selection: ConditionSelection = { excludes: {}, includes: {} };

    CONDITION_SELECTION_TYPES.forEach((selectionType) => {
      const rules: Record<string, unknown> = { ...ruleSet[selectionType] };

      // rule type keys (brand_ids, product_ids, lineages)
      Object.entries(rules).forEach(([key, value]) => {
        selection[selectionType] = updateConditionSelection(
          selection,
          selectionType,
          KEY_MAPPING[key] ?? key,
          toConditionValue(key, value)
        )[selectionType];
      });
    });

    if (ruleSet.product_threshold) {
      selection.includes['price'] = toRange(ruleSet.product_threshold);
    }

    return selection;
  });
};

const getApplyTo = (selection: ConditionSelection) => {
  if (selection.includes['product_ids']) {
    return 'products';
  }
  return 'attributes';
};

/**
 * Converts from special's rule_sets payload (after the "any") to the
 * ConditionSet which we need for the conditions components.
 */
export const convertRuleSetsToConditionSets = <R extends RuleSetLike>(
  ruleSets?: ['any', R, ...R[]]
): ConditionSet[] => {
  if (!ruleSets) {
    return EMPTY_CONDITION_SET;
  }
  return getConditionSelections(ruleSets).map((selections) => {
    return {
      applyTo: getApplyTo(selections),
      selections,
    };
  });
};

/** The rule sets one conditions editor owns, and the heading it renders under */
export type LabeledRuleSets = {
  conditionFor: string;
  ruleSets?: SpecialRuleSetsFor<SpecialTypeWithRules>;
};

// qualifier/discountable hold one section (spending_threshold) or an array of
// them (bundle), so coerce before reading the rule sets
const sectionRuleSets = (
  section?: SpecialRuleSection | SpecialRuleSection[] | null
) => (Array.isArray(section) ? section[0] : section)?.rule_sets;

/**
 * Splits a special's rules into the editors it needs — two for the types with
 * required and discounted rule_sets, one otherwise.
 */
export const getLabeledRuleSets = (
  rules: SpecialRules,
  specialType?: SpecialType | ''
): LabeledRuleSets[] => {
  if (specialType === 'bundle' || specialType === 'spending_threshold') {
    return [
      {
        conditionFor: 'required products',
        ruleSets: sectionRuleSets(rules.qualifier),
      },
      {
        conditionFor: 'discounted products',
        ruleSets: sectionRuleSets(rules.discountable),
      },
    ];
  }
  return [
    {
      conditionFor: specialType
        ? `${specialType.split('_').join(' ')} deal`
        : '',
      ruleSets: rules.rule_sets,
    },
  ];
};
